import { useState, useEffect, useContext, useRef, useCallback } from "react";
import MenuSideBar from "../components/MenuSideBar";
import { Button } from "react-bootstrap";
import StoreBlueprint from "../components/StoreBlueprint";
import { UserContext } from "../context/UserContext";
import { useFetchData } from "../helpers/FetchDataHelpers";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import LoadingPage from "./Loading";
import { FaSearch } from "react-icons/fa";
import DetailsPanel from "../components/DetailsPanel";
import SearchSideBar from "../components/SearchSideBar";

const POLLING_INTERVAL_MS = 5000;

// Cheap way to tell whether a poll actually changed anything before
// triggering a re-render. Relies on the backend (FastAPI/Pydantic) serializing
// fields in a deterministic order for the same response model, and on the
// underlying DB queries returning list items in a stable order across calls —
// if either assumption breaks, this stops detecting "no change" (silently
// falls back to always re-rendering, i.e. today's behavior without this
// optimization) rather than causing incorrect data.
const isSameData = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const StoreDeploymentPage = () => {
  // State variables
  const [showMenuSideBar, setShowMenuSideBar] = useState(false);
  const [showSearchSideBar, setShowSearchSideBar] = useState(false);
  const [deployments, setStoreDeployments] = useState(null);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [cameras, setCameras] = useState(null);
  const [gondolas, setGondolas] = useState(null);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { deploymentId } = useParams();
  const { token, userenv } = useContext(UserContext);
  const navigate = useNavigate();
  const fetchData = useFetchData();
  const canvasRef = useRef(null);

  // Always reflects the latest deployment/filter context (updated every
  // render, not via an effect, so it's never behind). Lets an in-flight
  // fetch tell, after it resolves, whether the user already switched
  // deployment/filter — if so, its response is stale and must be discarded
  // instead of overwriting state that a newer fetch already set correctly.
  const latestContextRef = useRef({ deploymentId: null, searchParamsString: "" });
  latestContextRef.current = {
    deploymentId: selectedDeployment?.deployment_id ?? null,
    searchParamsString: searchParams.toString(),
  };

  // Fetch deployments
  useEffect(() => {
    const fetchDeployments = async () => {
      const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}`;
      const { isResponseOK, data, errorMessage } = await fetchData(url);
      if (isResponseOK && data) {
        setStoreDeployments(data);
      } else {
        console.log(errorMessage);
      }
    };
    fetchDeployments();
  }, [token, userenv, navigate, fetchData]);

  // Set selected deployment
  useEffect(() => {
    if (!deployments || deployments.length === 0) return;
    if (deploymentId === "0") {
      setSelectedDeployment(deployments[0]);
      navigate(`/${deployments[0].deployment_id}`, { replace: true });
      return;
    }
    const deployment = deployments.filter(
      (d) => d.deployment_id.toString() === deploymentId,
    );
    if (deployment.length === 0) {
      navigate("/not-found");
    } else {
      setSelectedDeployment(deployment[0]);
    }
  }, [deployments, deploymentId, navigate]);

  const fetchCameras = useCallback(
    async (deploymentId) => {
      const requestSearchParamsString = searchParams.toString();
      let url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}/cameras`;
      if (Array.from(searchParams).length > 0) {
        const device = searchParams.get("device");
        if (device === "cameras") {
          url = url + "?" + searchParams.toString();
        }
      }
      const { isResponseOK, data, errorMessage } = await fetchData(url);

      const isStale =
        latestContextRef.current.deploymentId !== deploymentId ||
        latestContextRef.current.searchParamsString !== requestSearchParamsString;
      if (isStale) return;

      if (isResponseOK && data) {
        setCameras((prev) => (isSameData(prev, data) ? prev : data));
      } else {
        toast.error(`Error fetching cameras: ${errorMessage}`, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        console.log(errorMessage);
      }
    },
    [userenv, fetchData, searchParams],
  );

  useEffect(() => {
    if (!selectedDeployment) return;
    fetchCameras(selectedDeployment.deployment_id);
  }, [selectedDeployment, fetchCameras]);

  useEffect(() => {
    setSelectedCameraId(null);
  }, [selectedDeployment, searchParams]);

  const fetchGondolas = useCallback(
    async (deploymentId) => {
      const requestSearchParamsString = searchParams.toString();
      let url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}/gondolas`;

      if (Array.from(searchParams).length > 0) {
        const device = searchParams.get("device");
        if (device !== "cameras") {
          url = url + "?" + searchParams.toString();
        }
      }

      const { isResponseOK, data, errorMessage } = await fetchData(url);

      const isStale =
        latestContextRef.current.deploymentId !== deploymentId ||
        latestContextRef.current.searchParamsString !== requestSearchParamsString;
      if (isStale) return;

      if (isResponseOK && data) {
        setGondolas((prev) => (isSameData(prev, data) ? prev : data));
      } else {
        toast.error(`Error fetching gondolas: ${errorMessage}`, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        console.log(errorMessage);
      }
    },
    [userenv, fetchData, searchParams],
  );

  useEffect(() => {
    if (!selectedDeployment) return;
    const deploymentId = selectedDeployment.deployment_id;
    fetchGondolas(deploymentId);
  }, [selectedDeployment, fetchGondolas]);

  // Keeps cameras/gondolas in sync with edits made by other users, respecting the active filter.
  // Only polls while a filter is active — with no filter every camera/gondola renders
  // unfiltered regardless of what changes on the backend, so polling would be wasted work.
  useEffect(() => {
    if (!selectedDeployment || Array.from(searchParams).length === 0) return;
    const deploymentId = selectedDeployment.deployment_id;

    let inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        await Promise.allSettled([
          fetchCameras(deploymentId),
          fetchGondolas(deploymentId),
        ]);
      } finally {
        inFlight = false;
      }
    };

    let intervalId = null;
    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        void poll();
      }, POLLING_INTERVAL_MS);
    };
    const stopPolling = () => {
      clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void poll();
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === "visible") {
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedDeployment, searchParams, fetchCameras, fetchGondolas]);

  if (!deployments || !selectedDeployment || !cameras || !gondolas) {
    return <LoadingPage />;
  }

  return (
    <StoreBlueprint
      canvasRefParent={canvasRef}
      selectedDeployment={selectedDeployment}
      cameras={cameras.camera_circles}
      gondolas={gondolas.gondolas}
      setSelectedObject={setSelectedObject}
      selectedCameraId={selectedCameraId}
    >
      <img
        src="/sensei-logo-yellow.png"
        alt="Logo"
        style={{
          position: "absolute",
          top: "32px",
          left: "170px",
          zIndex: 10,
          width: "133px",
          height: "24px",
        }}
      />
      <Button
        variant="light"
        size="lg"
        onClick={() => {
          setShowMenuSideBar(true);
        }}
        style={{ position: "absolute", top: "20px", left: "20px", zIndex: 10 }}
      >
        {/* Hamburger Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="currentColor"
          className="bi bi-list"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
          />
        </svg>
      </Button>

      <Button
        variant="light"
        size="lg"
        onClick={() => {
          setShowSearchSideBar(true);
        }}
        style={{ position: "absolute", top: "20px", left: "95px", zIndex: 10 }}
      >
        <FaSearch />
      </Button>

      <MenuSideBar
        isVisible={showMenuSideBar}
        hideSideBar={() => {
          setShowMenuSideBar(false);
        }}
        selectedStoreName={selectedDeployment?.store_name}
        selectedDeploymentId={selectedDeployment.deployment_id}
        deployments={deployments}
      />

      <SearchSideBar
        isVisible={showSearchSideBar}
        hideSideBar={() => {
          setShowSearchSideBar(false);
        }}
        cameras={cameras}
        gondolas={gondolas}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        selectedCameraId={selectedCameraId}
        setSelectedCameraId={setSelectedCameraId}
      />

      <DetailsPanel
        isVisible={selectedObject !== null}
        hide={() => {
          setSelectedObject(null);
          canvasRef.current?.discardActiveObject();
          canvasRef.current?.requestRenderAll();
        }}
        selectedObject={selectedObject}
        deploymentId={selectedDeployment.deployment_id}
        refetchCameras={fetchCameras}
        refetchGondolas={fetchGondolas}
      />

      <ToastContainer />
    </StoreBlueprint>
  );
};

export default StoreDeploymentPage;
